import * as React from 'react';
import { useFiltersContext } from '../../contexts/filters';
import { ITaxonomy } from '../../types';
import { useSettingsContext } from '../../contexts/settings';
import sanitizeHtml from 'sanitize-html';

interface IProps {
	taxonomy: ITaxonomy;
	onTabChange?: (option: ISelectOption) => void;
}

interface ISelectOption {
	label: string;
	value: string;
	description: string;
	hide_filters: boolean; // eslint-disable-line camelcase
}

const Tabs = ({ taxonomy, onTabChange }: IProps) => {
	const options = taxonomy.terms.map((term) => {
		// decode htmlentities.
		const label = document.createElement('textarea');
		label.innerHTML = term.name;

		return {
			label: label.value,
			value: '' + term.term_id,
			description: term.description,
			hide_filters: term.hide_filters || false, // eslint-disable-line camelcase
		};
	});

	const { initSelectedTax, selectedTax, selectTaxTerm } = useFiltersContext();

	// Find matching option from selectedTax if it exists
	let initialOption = options[0];
	if (initSelectedTax[taxonomy.name]) {
		const matchingOption = options.find(
			(option) => option.value === String(selectedTax[taxonomy.name])
		);
		if (matchingOption) {
			initialOption = matchingOption;
		}
	}

	const [value, setValue] = React.useState(initialOption);
	const settings = useSettingsContext();

	const prevValue = React.useRef(initialOption.value);
	const handleOnChange = (option: ISelectOption | null) => {
		if (null !== option && prevValue.current !== option.value && selectTaxTerm) {
			setValue(option);
			selectTaxTerm(taxonomy.name, +option.value);
			prevValue.current = option.value;
			if (onTabChange) {
				onTabChange(option);
			}
		}
	};

	// When filters are reset, set our value back to the first option.
	React.useEffect(() => {
		if (settings.initSelectedTax === selectedTax) {
			setValue(initialOption);
		}
	}, [selectedTax]);

	React.useEffect(() => {
		if (onTabChange && initialOption) {
			onTabChange(initialOption);
		}
	}, []);

	return (
		<div className="pfa-filter-tabs">
			<div className="pfa-filter-tabs__items">
				{options.length > 1 &&
					options.map((option) => (
						<label
							key={option.value}
							className="pfa-filter-tabs__label btn"
							htmlFor={`pfa-filter-tabs-${taxonomy.name}-${option.value}`}
						>
							<input
								type="checkbox"
								id={`pfa-filter-tabs-${taxonomy.name}-${option.value}`}
								value={option.value}
								className="pfa-filter-tabs__input"
								checked={value.value === option.value}
								onChange={() => handleOnChange(option)}
							/>
							{option.label}
						</label>
					))}
			</div>
			<div className="pfa-filter-tabs__meta">
				<div
					className="pfa-filter-tabs__title"
					dangerouslySetInnerHTML={{ __html: sanitizeHtml(value.label) }}
				/>
				<div
					className="pfa-filter-tabs__description"
					dangerouslySetInnerHTML={{ __html: sanitizeHtml(value.description) }}
				/>
			</div>
		</div>
	);
};

Tabs.displayName = 'Tabs';

export default Tabs;
